import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ApiClient from '../../utils/ApiClient';

// Manual keyword + product-targeting campaign. The server builds a preview of what's missing for
// in-stock products (model, Schluter + model, EAN, size phrase; related-product pages; converting
// search terms), and creates only previewed items — nothing is sent until the confirm dialog is accepted.

const KINDS = { MODEL: 'Model', BRAND_MODEL: 'Schluter + model', EAN: 'EAN', SIZE: 'Size', STYLE: 'Shape/colour/pattern', SEARCH_TERM: 'Top search' };
const PAGE = 100;
const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);

function Modal({ title, children, onCancel, onConfirm, confirmLabel, busy }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
                <div className="text-sm text-gray-700 space-y-2">{children}</div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">Cancel</button>
                    <button onClick={onConfirm} disabled={busy} className="px-4 py-2 text-sm rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Tile({ label, value, hint }) {
    return (
        <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
            {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
        </div>
    );
}

function KeywordCampaign() {
    const [st, setSt] = useState(null);
    const [error, setError] = useState(null);
    const [excludedAsins, setExcludedAsins] = useState(new Set());
    const [excludedTerms, setExcludedTerms] = useState(new Set());
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/amazon-ads/keyword-campaign');
            setSt(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load the keyword campaign status.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Preview builds and applies both run on the server; poll while either is in flight.
    const working = st && (st.preparing || st.applying);
    useEffect(() => {
        if (!working) return undefined;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [working, load]);

    const previewId = st?.preview?.previewId;
    useEffect(() => { setExcludedAsins(new Set()); setExcludedTerms(new Set()); }, [previewId]);

    const build = async () => {
        setError(null);
        try {
            await ApiClient.post('/api/amazon-ads/keyword-campaign/preview');
            await load();
        } catch (err) {
            setError(err.response?.data?.error || 'Could not start the preview.');
        }
    };

    const apply = async () => {
        setBusy(true);
        setError(null);
        try {
            await ApiClient.post('/api/amazon-ads/keyword-campaign/apply', {
                previewId, excludeAsins: [...excludedAsins], excludeTerms: [...excludedTerms],
            });
            await load();
        } catch (err) {
            setError(err.response?.data?.error || 'Could not start creating.');
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    const p = st?.preview;
    const products = useMemo(() => {
        if (!p) return [];
        const q = search.trim().toLowerCase();
        if (!q) return p.products;
        return p.products.filter((r) => [r.asin, r.model, r.ean, r.title, ...r.skus.map((s) => s.sku)]
            .some((v) => v && String(v).toLowerCase().includes(q)));
    }, [p, search]);

    const selected = useMemo(() => {
        if (!p) return null;
        const prods = p.products.filter((r) => !excludedAsins.has(r.asin));
        const terms = p.topSearches.filter((t) => !excludedTerms.has(t.text));
        return {
            products: prods.length,
            keywords: prods.reduce((n, r) => n + r.keywords.length, 0) + terms.length,
            targets: prods.reduce((n, r) => n + r.targets.length, 0),
            terms: terms.length,
        };
    }, [p, excludedAsins, excludedTerms]);

    const toggle = (setter, key) => setter((s) => {
        const next = new Set(s);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    if (!st) return error ? <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">{error}</div>
        : <div className="text-gray-500">Loading…</div>;

    const r = st.lastApply;
    const c = (o) => (o ? o.ok : 0);

    return (
        <div>
            {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">{error}</div>}
            {st.applying && (
                <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800 flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Creating on Amazon on the server
                    {st.progress && ` — ${st.progress.done.toLocaleString()} of ${st.progress.total.toLocaleString()} sent${st.progress.startedBy ? ` (started by ${st.progress.startedBy})` : ''}`}.
                    You can leave this page; it keeps running.
                </div>
            )}
            {r && !st.applying && (
                <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${r.error || r.errorCount ? 'bg-yellow-50 border-yellow-200 text-yellow-900' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    {r.error ? `Create failed: ${r.error}` : (
                        <>
                            Last run{r.finishedAt && ` (${String(r.finishedAt).replace('T', ' ').slice(0, 16)}${r.startedBy ? `, ${r.startedBy}` : ''})`}:
                            {r.campaignCreated && ' campaign created,'} {c(r.adGroups)} ad groups, {c(r.ads)} ads, {c(r.keywords)} keywords, {c(r.targets)} product targets.
                            {r.errorCount > 0 && ` ${r.errorCount} rejected by Amazon.`}
                            {r.skipped > 0 && ` ${r.skipped} skipped (unticked, or no longer applied when re-checked).`}
                            {r.errors?.length > 0 && <ul className="mt-1 list-disc ml-5">{r.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
                        </>
                    )}
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-start justify-between gap-4">
                <div className="text-sm text-gray-600 max-w-3xl space-y-1">
                    <p>
                        A manual campaign next to the auto one. Each in-stock product gets a <b>keyword</b> ad group (Schluter model,
                        "schluter" + model, and EAN as exact match at {money(p?.campaign?.bidExact ?? 0.75)}; type + size and shape / colour / pattern phrases as broad
                        match at {money(p?.campaign?.bidOther ?? 0.65)}) and a <b>product-targeting</b> ad group (its ad on related
                        products' pages at {money(p?.campaign?.bidOther ?? 0.65)}). A shared "Top searches" group bids exact on every
                        search that produced an order in the last 30 days.
                    </p>
                    <p>Only missing pieces are proposed: running this again later adds newly stocked products. Out-of-stock ads are paused from the Stock-aware ads tab.</p>
                </div>
                <button
                    onClick={build}
                    disabled={st.preparing || st.applying}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                    {st.preparing && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                    {st.preparing ? 'Building preview… (a few minutes)' : (p ? 'Rebuild preview' : 'Build preview')}
                </button>
            </div>

            {st.prepareError && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">Preview failed: {st.prepareError}</div>}

            {p && (
                <>
                    {p.stockStale && (
                        <div className="mb-4 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">
                            <ExclamationTriangleIcon className="h-5 w-5" />
                            Stock data is stale (FBA {p.stockAsOf || 'never'}, SFP/MER {p.mfnStockAsOf || 'never'}) — creating is blocked until it loads.
                        </div>
                    )}
                    <p className="text-xs text-gray-500 mb-3">
                        Preview built {String(p.builtAt).replace('T', ' ').slice(0, 16)} · stock FBA {p.stockAsOf}, SFP/MER {p.mfnStockAsOf}.
                        {' '}Campaign <b>{p.campaign.name}</b> — {p.campaign.create ? `will be created: ${money(p.campaign.dailyBudget)}/day, bids down only` : 'exists'}.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <Tile label="Products" value={p.products.length.toLocaleString()} hint={`${selected.products} selected`} />
                        <Tile label="Ad groups" value={p.counts.adGroups.toLocaleString()} />
                        <Tile label="Ads" value={p.counts.ads.toLocaleString()} hint={`${p.topSearchAds} in Top searches`} />
                        <Tile
                            label="Keywords"
                            value={p.counts.keywords.toLocaleString()}
                            hint={Object.entries(p.counts.keywordsByKind).map(([k, n]) => `${KINDS[k] || k} ${n}`).join(' · ')}
                        />
                        <Tile label="Product targets" value={p.counts.targets.toLocaleString()} />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="text-sm text-gray-700">
                            Selected: <b>{selected.products}</b> products · <b>{selected.keywords.toLocaleString()}</b> keywords · <b>{selected.targets.toLocaleString()}</b> product targets
                        </div>
                        <button
                            onClick={() => setConfirming(true)}
                            disabled={p.counts.total === 0 || st.applying || p.stockStale}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            Review &amp; create
                        </button>
                    </div>

                    {p.topSearches.length > 0 && (
                        <div className="bg-white rounded-lg shadow mb-6">
                            <div className="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-700">
                                Top searches — exact match at {money(p.campaign.bidOther)} ({p.topSearches.length})
                            </div>
                            <div className="overflow-x-auto max-h-80">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 w-8" />
                                            <th className="px-3 py-2 text-left font-medium text-gray-600">Search</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600">Clicks (30d)</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600">Orders</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600">Sales</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {p.topSearches.map((t) => (
                                            <tr key={t.text} className="hover:bg-gray-50">
                                                <td className="px-3 py-1.5">
                                                    <input type="checkbox" checked={!excludedTerms.has(t.text)} onChange={() => toggle(setExcludedTerms, t.text)} />
                                                </td>
                                                <td className="px-3 py-1.5 text-gray-800">{t.text}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{t.clicks}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{t.orders}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{money(t.sales)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow mb-6">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
                            <h2 className="text-sm font-semibold text-gray-700">Products ({products.length.toLocaleString()})</h2>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setExcludedAsins(new Set())} className="text-xs text-blue-600 hover:underline">Select all</button>
                                <button onClick={() => setExcludedAsins(new Set(p.products.map((x) => x.asin)))} className="text-xs text-blue-600 hover:underline">Select none</button>
                                <input
                                    value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ASIN, model, EAN, SKU, title"
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1 w-64"
                                />
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 w-8" />
                                        <th className="px-3 py-2 text-left font-medium text-gray-600">ASIN</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Title</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600">New keywords</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600">Targets</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600">SKUs</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {products.slice(0, PAGE).map((row) => (
                                        <React.Fragment key={row.asin}>
                                            <tr className="hover:bg-gray-50 align-top">
                                                <td className="px-3 py-1.5">
                                                    <input type="checkbox" checked={!excludedAsins.has(row.asin)} onChange={() => toggle(setExcludedAsins, row.asin)} />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <a href={`https://amazon.com/dp/${row.asin}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{row.asin}</a>
                                                    {row.model && <div className="font-mono text-xs text-gray-500">{row.model}</div>}
                                                </td>
                                                <td className="px-3 py-1.5 max-w-xs truncate text-gray-700" title={row.title || ''}>{row.title || '—'}</td>
                                                <td className="px-3 py-1.5">
                                                    <div className="flex flex-wrap gap-1">
                                                        {row.keywords.map((k) => (
                                                            <span key={k.text + k.matchType} title={`${KINDS[k.kind] || k.kind} · ${k.matchType} · ${money(k.bid)}`}
                                                                className={`text-xs px-1.5 py-0.5 rounded ${k.matchType === 'EXACT' ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'}`}>
                                                                {k.text}
                                                            </span>
                                                        ))}
                                                        {row.keywords.length === 0 && <span className="text-xs text-gray-400">—</span>}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">
                                                    {row.targets.length > 0
                                                        ? <button onClick={() => setOpen(open === row.asin ? null : row.asin)} className="text-blue-600 hover:underline">{row.targets.length}</button>
                                                        : '—'}
                                                </td>
                                                <td className="px-3 py-1.5 text-right text-xs text-gray-600 whitespace-nowrap">
                                                    {row.skus.map((s) => <div key={s.sku}><span className="font-mono">{s.sku}</span> · {s.qty} {s.channel === 'FBA' ? 'FBA' : 'SFP/MER'}</div>)}
                                                </td>
                                            </tr>
                                            {open === row.asin && (
                                                <tr>
                                                    <td />
                                                    <td colSpan={5} className="px-3 pb-3">
                                                        <div className="text-xs text-gray-500 mb-1">Ad shows on these product pages ({money(row.targets[0]?.bid)} bid):</div>
                                                        <ul className="text-xs text-gray-700 space-y-0.5">
                                                            {row.targets.map((t) => (
                                                                <li key={t.asin}>
                                                                    <a href={`https://amazon.com/dp/${t.asin}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{t.asin}</a> {t.title}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                            {products.length > PAGE && (
                                <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                                    Showing {PAGE} of {products.length.toLocaleString()} — search to narrow. Selection covers all rows, not just the ones shown.
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {confirming && p && (
                <Modal title="Create these on Amazon?" confirmLabel="Create — this spends money" busy={busy} onCancel={() => setConfirming(false)} onConfirm={apply}>
                    <ul className="list-disc ml-5">
                        {p.campaign.create && <li>New campaign <b>{p.campaign.name}</b>: manual, <b>{money(p.campaign.dailyBudget)}/day</b> budget, dynamic bids down only.</li>}
                        <li><b>{selected.products}</b> products: keyword and product-targeting ad groups with their ads.</li>
                        <li><b>{selected.keywords.toLocaleString()}</b> keywords — exact at {money(p.campaign.bidExact)} (model, EAN), broad and top searches at {money(p.campaign.bidOther)}.</li>
                        <li><b>{selected.targets.toLocaleString()}</b> product-page targets at {money(p.campaign.bidOther)}.</li>
                    </ul>
                    <p>Ads start serving as soon as Amazon accepts them. Spend is capped by the campaign's daily budget, on top of the auto campaign's.</p>
                    <p>Each item is re-checked against current stock before it's sent. This runs on the server — you can leave the page once it starts.</p>
                </Modal>
            )}
        </div>
    );
}

export default KeywordCampaign;
